import { useEffect, useState } from "react";

import { Button, Callout, InputGroup, Intent } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { StyledFormGroup } from "@/components/pages_logged_in/program/components/set-components/StyledFormGroup";
import { Tiptap } from "@/components/pieces/editor/tiptap";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { EvaluationCriterionFieldsFragment } from "@/graphql";

interface AddBehaviorItemDialogProps {
  open: boolean;
  onClose: () => void;
  criterion: EvaluationCriterionFieldsFragment | null;
  onSave: (
    criterionId: string,
    item: { name: string; description: string },
  ) => void;
  loading?: boolean;
  errorMessage?: string;
}

export function AddBehaviorItemDialog({
  open,
  onClose,
  criterion,
  onSave,
  loading = false,
  errorMessage = "",
}: AddBehaviorItemDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
    }
  }, [open]);

  const handleClose = () => {
    setName("");
    setDescription("");
    onClose();
  };

  const handleSave = () => {
    if (criterion && name.trim()) {
      onSave(criterion.id, {
        name: name.trim(),
        description,
      });
    }
  };

  return (
    <StyledDialog
      isOpen={open}
      onClose={handleClose}
      lazy={true}
      title={<Box fontSize={18}>Add Behavior Item</Box>}
      width="650px"
    >
      <>
        <div className="flex flex-col px-6 gap-6 py-4">
          {errorMessage && (
            <Box mb={2}>
              <Callout icon={null} intent={Intent.DANGER}>
                {errorMessage}
              </Callout>
            </Box>
          )}

          <StyledFormGroup
            label="Behavior Label"
            subLabel="Describe what success looks like"
          >
            <InputGroup
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Restate customer concerns"
              large
              fill
              autoFocus
            />
          </StyledFormGroup>

          <StyledFormGroup
            label="Evaluation Guide"
            subLabel="Help our AI understand how to evaluate this behavior. Be specific and concrete about what success looks like."
          >
            <Tiptap
              key={open ? "open" : "closed"}
              content={description}
              onChange={(value) => setDescription(value)}
              placeholder="Break down how to evaluate this behavior. Include both what to look for and what to avoid."
            />
          </StyledFormGroup>
        </div>

        <Flex className="border-border sticky bottom-0 z-10 w-full rounded-b-xl border-t bg-white">
          <Box className="bbs" />
          <Box py={3} px={24}>
            <Button
              intent={Intent.PRIMARY}
              onClick={handleSave}
              loading={loading}
              disabled={!name.trim()}
              large
              style={{ minWidth: 100 }}
            >
              Add Item
            </Button>
            <Button onClick={handleClose} large className="ml-2">
              Cancel
            </Button>
          </Box>
        </Flex>
      </>
    </StyledDialog>
  );
}
